import React, { useState, useRef, useEffect, ForwardedRef } from 'react'
import styled from 'styled-components'
import { mapLanguageToComponentProps } from 'i18n'
import { connect } from 'react-redux'
import { onClickEvent } from 'analytics/performance'
import { ButtonContent } from 'components/styled/button/button-content'
import { I18nComponentType } from 'i18n-types'
import {
  BaseButtonProps,
  ButtonBox,
  BaseButtonStyle,
  BaseButtonStyleProps,
} from './base-button'

const INITIAL_COUNT = 3
const INITIAL_RESET_COUNT = 10

export interface ButtonEventProps {
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void
  onMouseMove?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onPointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void
  onPointerEnter?: (event: React.PointerEvent<HTMLButtonElement>) => void
  onPointerLeave?: (event: React.PointerEvent<HTMLButtonElement>) => void
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void
  onKeyUp?: (event: React.KeyboardEvent<HTMLButtonElement>) => void
}

export interface ButtonProps extends ButtonEventProps, BaseButtonProps {
  disabled?: boolean
  loading?: boolean
  dispatch?: any
  type?: 'button' | 'submit'
  'aria-describedby'?: string
  buttonRef?: ForwardedRef<HTMLButtonElement>
  useConfirm?: boolean
  tabIndex?: number // in some special cases tabIndex can be modified (e.g. button is visually hidden)
  flippedIcon?: boolean
}

export const StyledButton = styled.button<BaseButtonStyleProps>`
  ${BaseButtonStyle};
`

export const ButtonFC = ({
  i18n,
  disabled = false,
  loading = false,
  onClick,
  event,
  type = 'button',
  variant = 'primary',
  icon,
  iconRight,
  useOutlinedIcon,
  svgIcon,
  text,
  size = 'default',
  label,
  buttonRef,
  useConfirm = false,
  tabIndex,
  flippedIcon = false,
  'aria-describedby': ariaDescribedBy,
  onBlur,
  onFocus,
  onMouseMove,
  onMouseDown,
  onPointerDown,
  onPointerEnter,
  onPointerLeave,
  onKeyDown,
  onKeyUp,
  dispatch,
  language,
  ...props
}: ButtonProps & { i18n: I18nComponentType; language?: string }) => {
  const [count, setCount] = useState<number>(INITIAL_COUNT)
  const [confirmPhase, setConfirmPhase] = useState<
    'initial' | 'countdown' | 'confirm'
  >('initial')
  const timerRef = useRef<null | ReturnType<typeof setInterval>>(null)

  const onButtonClick = (mouseEvent: React.MouseEvent<HTMLButtonElement>) => {
    if (event) onClickEvent(event)
    if (onClick) onClick(mouseEvent)
  }

  const buttonText = () => {
    if (useConfirm && confirmPhase === 'countdown') {
      return i18n.confirmButton.wait(count)
    }

    if (useConfirm && confirmPhase === 'confirm') {
      return i18n.confirmButton.confirm
    }
    return text
  }

  // Handlers for if useConfirm is true
  // Resets the countdown button after 10 seconds
  const startConfirm = () => {
    let counter = INITIAL_RESET_COUNT
    setConfirmPhase('confirm')
    timerRef.current = setInterval(() => {
      counter -= 1
      if (counter === 0) {
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
        setConfirmPhase('initial')
      }
    }, 1000)
  }

  const startCountdown = () => {
    let counter = count
    setConfirmPhase('countdown')
    setCount(counter)
    timerRef.current = setInterval(() => {
      setCount(counter - 1)
      counter -= 1
      if (counter === 0) {
        setCount(INITIAL_COUNT)
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
        startConfirm()
      } else {
        setCount(counter)
      }
    }, 1000)
  }

  const onConfirmButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!useConfirm || confirmPhase !== 'initial') {
      onButtonClick(event)
      setConfirmPhase('initial')
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    } else {
      startCountdown()
    }
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  const isLoading = (useConfirm && confirmPhase === 'countdown') || loading
  return (
    <ButtonBox {...props}>
      <StyledButton
        $variant={variant}
        $size={size}
        $hasText={!!text}
        $loading={!!isLoading}
        aria-label={label}
        ref={buttonRef}
        tabIndex={tabIndex}
        /* Button specific props */
        disabled={
          !!(
            (useConfirm && confirmPhase === 'countdown') ||
            disabled ||
            loading
          )
        }
        type={type}
        onClick={onConfirmButtonClick}
        onBlur={onBlur}
        onFocus={onFocus}
        onMouseMove={onMouseMove}
        onMouseDown={onMouseDown}
        onPointerDown={onPointerDown}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        aria-describedby={ariaDescribedBy}
      >
        <ButtonContent
          variant={variant}
          icon={icon}
          iconRight={iconRight}
          useOutlinedIcon={useOutlinedIcon}
          svgIcon={svgIcon}
          text={buttonText()}
          loading={isLoading}
          size={size}
          flippedIcon={flippedIcon}
        />
      </StyledButton>
    </ButtonBox>
  )
}

const ConnectedButton = connect<{ i18n: I18nComponentType }>(
  mapLanguageToComponentProps()
)(ButtonFC)

export const Button = React.forwardRef(
  (props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => (
    <ConnectedButton {...props} buttonRef={ref} />
  )
)
